import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
  Collapse,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import HeroSection from '../components/common/HeroSection';
import SectionTitle from '../components/common/SectionTitle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { courseGroups, courseDetails } from '../data/courses';

const CoursesPage = () => {
  const [expandedId, setExpandedId] = useState(null);

  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  return (
    <Box>
      <HeroSection
        title="Our Courses"
        subtitle="Comprehensive Chinese language programmes from K2 to Secondary 4"
      />

      <Box sx={{ py: { xs: 6, md: 10 }, bgcolor: 'background.default' }}>
        <Container maxWidth="lg">
          <SectionTitle
            title="Course Categories"
            subtitle="We offer four core areas of Chinese language learning"
          />

          <Grid container spacing={4}>
            {courseGroups.map((group) => {
              const details = courseDetails.find((d) => d.id === group.id);
              const isExpanded = expandedId === group.id;

              return (
                <Grid item xs={12} md={6} key={group.id}>
                  <Card
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      overflow: 'visible',
                      position: 'relative',
                    }}
                  >
                    <Box sx={{ position: 'relative' }}>
                      <CardMedia
                        component="img"
                        height="220"
                        image={group.image}
                        alt={group.titleEn}
                        sx={{ objectFit: 'cover' }}
                      />
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 16,
                          left: 16,
                        }}
                      >
                        <Chip
                          label={group.titleZh}
                          sx={{
                            bgcolor: group.color,
                            color: '#fff',
                            fontWeight: 600,
                            fontSize: '0.9rem',
                          }}
                        />
                      </Box>
                    </Box>

                    <CardContent sx={{ flexGrow: 1, p: 3 }}>
                      <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                        {group.titleEn}
                      </Typography>
                      <Typography
                        variant="body1"
                        color="text.secondary"
                        sx={{ mb: 2, lineHeight: 1.7 }}
                      >
                        {group.description}
                      </Typography>

                      <Button
                        onClick={() => toggleExpand(group.id)}
                        endIcon={isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        sx={{ mb: 1, fontWeight: 600 }}
                      >
                        {isExpanded ? 'Hide Topics' : 'View Topics'}
                      </Button>

                      <Collapse in={isExpanded}>
                        <List dense sx={{ bgcolor: 'rgba(0,0,0,0.02)', borderRadius: 2, py: 1 }}>
                          {details?.content.map((item, idx) => (
                            <ListItem key={idx} sx={{ py: 0.5 }}>
                              <ListItemIcon sx={{ minWidth: 32 }}>
                                <CheckCircleIcon
                                  sx={{ fontSize: 18, color: group.color }}
                                />
                              </ListItemIcon>
                              <ListItemText
                                primary={
                                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {item.zh} — {item.en}
                                  </Typography>
                                }
                              />
                            </ListItem>
                          ))}
                        </List>
                      </Collapse>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>

          {/* CTA */}
          <Box
            sx={{
              mt: 8,
              p: { xs: 4, md: 6 },
              borderRadius: 4,
              background: 'linear-gradient(135deg, #FF6F00 0%, #FFA040 100%)',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h4"
              sx={{ color: '#fff', fontWeight: 700, mb: 2, fontSize: { xs: '1.5rem', md: '2rem' } }}
            >
              Find the Right Class for Your Child
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: 'rgba(255,255,255,0.9)', mb: 3, maxWidth: 500, mx: 'auto' }}
            >
              Check our 2027 schedule and enroll in the class that suits your child&apos;s level.
            </Typography>
            <Button
              variant="contained"
              size="large"
              component={RouterLink}
              to="/schedule"
              startIcon={<CalendarMonthIcon />}
              sx={{
                bgcolor: '#fff',
                color: 'primary.main',
                fontWeight: 700,
                '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
                boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
              }}
            >
              View Schedule
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default CoursesPage;
